import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';

/**
 * React Query hooks for the ecommerce home-dashboard widgets. These wrap the
 * existing `/admin/dashboard*` endpoints (functions/src/routes/admin/dashboard.ts).
 * Extracted from the old pages/admin/Dashboard.tsx inline fetches so both the
 * widgets and any future consumer share one cache entry.
 */

export interface DashboardStats {
  revenue_today: number;
  revenue_week: number;
  revenue_month: number;
  revenue_trends: {
    today_change: number;
    week_change: number;
    month_change: number;
  };
  order_counts: {
    pending: number;
    processing: number;
    shipped: number;
    delivered: number;
    cancelled: number;
  };
  new_customers_month: number;
  customer_trend: number;
  low_stock_count: number;
}

export interface RecentOrder {
  id: string;
  order_number: string;
  customer_name: string;
  customer_email: string;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  total: number;
  items_count: number;
  created_at: string;
}

export interface TopProduct {
  id: string;
  name: string;
  primary_image_url: string | null;
  units_sold: number;
  revenue: number;
}

export interface LowStockItem {
  product_id: string;
  variant_id: string;
  product_name: string;
  variant_name: string | null;
  sku: string;
  current_stock: number;
  threshold: number;
}

const STALE = 60 * 1000;

export function useEcomStats() {
  return useQuery({
    queryKey: ['dashboard', 'ecom', 'stats'],
    queryFn: () => api.get<DashboardStats>('/admin/dashboard'),
    staleTime: STALE,
  });
}

export function useRecentOrders() {
  return useQuery({
    queryKey: ['dashboard', 'ecom', 'recent-orders'],
    queryFn: async () =>
      (await api.get<{ orders: RecentOrder[] }>('/admin/dashboard/recent-orders')).orders,
    staleTime: STALE,
  });
}

export function useTopProducts() {
  return useQuery({
    queryKey: ['dashboard', 'ecom', 'top-products'],
    queryFn: async () =>
      (await api.get<{ products: TopProduct[] }>('/admin/dashboard/top-products')).products,
    staleTime: STALE,
  });
}

export function useLowStock() {
  return useQuery({
    queryKey: ['dashboard', 'ecom', 'low-stock'],
    queryFn: async () =>
      (await api.get<{ items: LowStockItem[] }>('/admin/dashboard/low-stock')).items,
    staleTime: STALE,
  });
}
